import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { Loader2, MoreHorizontal, UserCheck, UserX, Store, ShieldCheck, ShieldX, ShieldAlert, User, Building, FileText } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import SearchFilterBar from '../profile/SearchFilterBar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import UserActionDialog from './UserActionDialog';

// Define store_approval_status_enum type based on your Supabase enum
type StoreApprovalStatus = 'pending' | 'approved' | 'rejected' | null;
type ActionType = 'block' | 'unblock' | 'makeAdmin' | 'removeAdmin' | 'approveStore' | 'rejectStore' | 'markPending' | 'convertToUser';

interface UserProfile {
  id: string;
  username: string | null;
  email: string | null;
  full_name: string | null;
  created_at: string;
  role: string;
  is_blocked: boolean;
  is_store: boolean;
  store_approval_status: StoreApprovalStatus;
  store_name: string | null;
  store_description: string | null;
}

interface DialogState {
  isOpen: boolean;
  actionType: ActionType | null;
  user: UserProfile | null;
}

const UserManagement = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [dialogState, setDialogState] = useState<DialogState>({
    isOpen: false,
    actionType: null,
    user: null
  });

  const resetFilters = () => {
    setSearchTerm('');
    setStartDate(undefined);
    setEndDate(undefined);
  };

  // Fetch users from profiles table
  const { data: users = [], isLoading } = useQuery({
    queryKey: ['adminUsers', searchTerm, startDate, endDate],
    queryFn: async () => {
      try {
        let query = supabase
          .from('profiles')
          .select('*, store_name, store_description')
          .order('created_at', { ascending: false });
        
        if (searchTerm) {
          query = query.or(`username.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,full_name.ilike.%${searchTerm}%,store_name.ilike.%${searchTerm}%`);
        }
        
        if (startDate) {
          query = query.gte('created_at', startDate.toISOString());
        }
        
        if (endDate) {
          const nextDay = new Date(endDate);
          nextDay.setDate(nextDay.getDate() + 1);
          query = query.lt('created_at', nextDay.toISOString());
        }
        
        const { data, error } = await query;
        
        if (error) throw error;
        return (data || []) as UserProfile[];
      } catch (error) {
        console.error('Error fetching users:', error);
        return [];
      }
    },
  });

  // Generic mutation for all user actions
  const userActionMutation = useMutation({
    mutationFn: async ({ 
      userId, 
      actionType, 
      adminNotes 
    }: { 
      userId: string; 
      actionType: ActionType; 
      adminNotes: string;
    }) => {
      let updateData: any = {};
      let successMessage = '';

      switch (actionType) {
        case 'block':
          updateData = { is_blocked: true };
          successMessage = 'User has been blocked successfully.';
          break;
        case 'unblock':
          updateData = { is_blocked: false };
          successMessage = 'User has been unblocked successfully.';
          break;
        case 'makeAdmin':
          updateData = { role: 'admin' };
          successMessage = 'User has been granted admin rights.';
          break;
        case 'removeAdmin':
          updateData = { role: 'user' };
          successMessage = 'Admin rights have been removed from user.';
          break;
        case 'approveStore':
          updateData = { store_approval_status: 'approved' };
          successMessage = 'Store has been approved.';
          break;
        case 'rejectStore':
          updateData = { store_approval_status: 'rejected' };
          successMessage = 'Store has been rejected.';
          break;
        case 'markPending':
          updateData = { store_approval_status: 'pending' };
          successMessage = 'Store status has been set to pending.';
          break;
        case 'convertToUser':
          updateData = { store_approval_status: null };
          successMessage = 'User has been converted to regular user.';
          break;
        default:
          throw new Error('Invalid action type');
      }

      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', userId);
      
      if (error) throw error;
      
      // TODO: Log admin action with notes if needed
      // await logAdminAction(userId, actionType, adminNotes);
      
      return { userId, actionType, successMessage };
    },
    onSuccess: ({ successMessage }) => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      toast({
        title: 'Action Completed',
        description: successMessage,
      });
      setDialogState({ isOpen: false, actionType: null, user: null });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to perform action.',
        variant: 'destructive',
      });
    },
  });

  const openActionDialog = (user: UserProfile, actionType: ActionType) => {
    setDialogState({
      isOpen: true,
      actionType,
      user
    });
  };

  const handleDialogConfirm = (adminNotes: string) => {
    if (dialogState.user && dialogState.actionType) {
      userActionMutation.mutate({
        userId: dialogState.user.id,
        actionType: dialogState.actionType,
        adminNotes
      });
    }
  };

  const handleDialogClose = () => {
    setDialogState({ isOpen: false, actionType: null, user: null });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('sq-AL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStoreStatusBadge = (status: StoreApprovalStatus, isStore: boolean) => {
    if (status === 'approved') {
      return <Badge className="bg-green-100 text-green-700 border-green-300"><ShieldCheck className="mr-1 h-3 w-3" />Approved Store</Badge>;
    }
    if (status === 'pending') {
      return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300"><ShieldAlert className="mr-1 h-3 w-3" />Pending Approval</Badge>;
    }
    if (status === 'rejected') {
      return <Badge variant="destructive" className="bg-red-100 text-red-700 border-red-300"><ShieldX className="mr-1 h-3 w-3" />Rejected Store</Badge>;
    }
    if (isStore) {
        return <Badge variant="secondary"><Store className="mr-1 h-3 w-3" />Store (Legacy/Manual)</Badge>;
    }
    return <Badge variant="outline">Regular User</Badge>;
  };

  const truncateText = (text: string | null | undefined, maxLength: number) => {
    if (!text) return 'N/A';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };
  
  return (
    <>
      <Card className="w-full">
        <CardHeader>
          <CardTitle>User Management</CardTitle>
          <CardDescription>
            View and manage all users of the platform, including store approval status and details.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <SearchFilterBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            onReset={resetFilters}
          />
          
          {isLoading ? (
            <div className="flex justify-center my-8">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No users found matching your criteria
            </div>
          ) : (
            <div className="overflow-x-auto">
              <TooltipProvider>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User Info</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Account Status</TableHead>
                      <TableHead>Store Status</TableHead>
                      <TableHead>Store Details</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">
                          <div>
                            <div className="font-medium">{user.full_name || 'No Name'}</div>
                            <div className="text-sm text-gray-500">
                              {user.username ? '@' + user.username : ''}
                            </div>
                            <div className="text-xs text-gray-500">{user.email}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={user.role === 'admin' ? 'default' : 'outline'}>
                            {user.role === 'admin' ? 'Admin' : 'User'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {user.is_blocked ? (
                            <Badge variant="destructive">Blocked</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200">Active</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {getStoreStatusBadge(user.store_approval_status, user.is_store)}
                        </TableCell>
                        <TableCell>
                          {user.store_approval_status === 'pending' || user.store_name ? (
                            <div className="space-y-1">
                              {user.store_name && (
                                <div className="flex items-center text-sm">
                                  <Building size={14} className="mr-1.5 text-gray-500" /> 
                                  <strong>{truncateText(user.store_name, 25)}</strong>
                                </div>
                              )}
                              {user.store_description && (
                                 <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="flex items-start text-xs text-gray-600 cursor-pointer">
                                      <FileText size={12} className="mr-1.5 mt-0.5 text-gray-400 flex-shrink-0" /> 
                                      <span>{truncateText(user.store_description, 30)}</span>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" align="start" className="max-w-xs bg-background text-foreground border shadow-lg rounded-md p-2">
                                    <p className="text-sm whitespace-pre-wrap">{user.store_description}</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              {!user.store_name && !user.store_description && user.store_approval_status === 'pending' && (
                                  <span className="text-xs text-gray-400 italic">No details provided</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">N/A</span>
                          )}
                        </TableCell>
                        <TableCell>{formatDate(user.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>User Actions</DropdownMenuLabel>
                              <DropdownMenuItem onClick={() => openActionDialog(user, user.is_blocked ? 'unblock' : 'block')}>
                                {user.is_blocked ? (
                                  <><UserCheck className="mr-2 h-4 w-4" /> Unblock User</>
                                ) : (
                                  <><UserX className="mr-2 h-4 w-4" /> Block User</>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openActionDialog(user, user.role === 'admin' ? 'removeAdmin' : 'makeAdmin')}>
                                {user.role === 'admin' ? 
                                  <><User className="mr-2 h-4 w-4" /> Remove Admin Rights</> : 
                                  <><UserCheck className="mr-2 h-4 w-4" /> Make Admin</>}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuLabel>Store Actions</DropdownMenuLabel>
                              {user.store_approval_status !== 'approved' && (
                                <DropdownMenuItem onClick={() => openActionDialog(user, 'approveStore')}>
                                  <ShieldCheck className="mr-2 h-4 w-4 text-green-500" /> Approve Store
                                </DropdownMenuItem>
                              )}
                              {user.store_approval_status !== 'rejected' && (
                                <DropdownMenuItem onClick={() => openActionDialog(user, 'rejectStore')}>
                                  <ShieldX className="mr-2 h-4 w-4 text-red-500" /> Reject Store
                                </DropdownMenuItem>
                              )}
                              {user.store_approval_status !== 'pending' && user.store_approval_status !== null && (
                                <DropdownMenuItem onClick={() => openActionDialog(user, 'markPending')}>
                                  <ShieldAlert className="mr-2 h-4 w-4 text-yellow-500" /> Mark as Pending
                                </DropdownMenuItem>
                              )}
                               {user.store_approval_status !== null && (
                                <DropdownMenuItem onClick={() => openActionDialog(user, 'convertToUser')}>
                                  <User className="mr-2 h-4 w-4" /> Convert to Regular User
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TooltipProvider>
            </div>
          )}
        </CardContent>
      </Card>

      <UserActionDialog
        isOpen={dialogState.isOpen}
        onClose={handleDialogClose}
        onConfirm={handleDialogConfirm}
        user={dialogState.user}
        actionType={dialogState.actionType}
        isProcessing={userActionMutation.isPending}
      />
    </>
  );
};

export default UserManagement;
